/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2023 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2023 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { Grid } from '@mui/material';
import type { TFunction } from 'i18next';
import React from 'react';
import { CustomAccordion, CustomIconButton } from '@opengeoweb/shared';
import {
  ArrowDropDownDown,
  Copy,
  Delete,
  Visibility,
  VisibilityOff,
} from '@opengeoweb/theme';
import { ParameterInfoButtonConnect } from './ParameterInfo/ParameterInfoButtonConnect';
import ParameterColorSelect from './TimeSeriesManagerParameterComponents/ParameterColorSelect';
import ParameterDimensionSelect from './TimeSeriesManagerParameterComponents/ParameterDimensionSelect';
import ParameterOpacitySelect from './TimeSeriesManagerParameterComponents/ParameterOpacitySelect';
import ParameterTypeSelect from './TimeSeriesManagerParameterComponents/ParameterTypeSelect';
import type { Parameter, ParameterListStyles } from '../TimeSeries/types';
import ParameterNameSelect from './TimeSeriesManagerParameterComponents/ParameterNameSelect';

const TimeSeriesManagerRowAccordion: React.FC<{
  panelId: string;
  plotIsEnabled: boolean;
  rowIsEnabled: boolean;
  parameter: Parameter;
  styles: ParameterListStyles;
  addParameter: (parameter: Parameter) => void;
  patchParameter: (parameter: Partial<Parameter>) => void;
  deleteParameter: (id: string) => void;
  toggleParameter: (id: string) => void;
  t: TFunction;
}> = ({
  panelId,
  plotIsEnabled,
  rowIsEnabled,
  parameter,
  styles,
  addParameter,
  patchParameter,
  deleteParameter,
  toggleParameter,
  t,
}) => {
  return (
    <CustomAccordion
      CustomExpandIcon={ArrowDropDownDown}
      data-testid="timeSeriesManagerRowAccordion"
      defaultExpanded={false}
      sx={{
        mx: 0,
        width: '100%',
        alignItems: 'center',
        '.MuiAccordionSummary-content': {
          margin: 0,
        },
        backgroundColor: plotIsEnabled
          ? 'geowebColors.layerManager.tableRowDefaultCardContainer.fill'
          : 'geowebColors.layerManager.tableRowDisabledCardContainer.fill',
      }}
      title={
        <Grid
          alignItems="center"
          container
          size="grow"
          spacing={2}
          sx={{ pl: '14px' }}
        >
          <Grid>
            <CustomIconButton
              data-testid="accordionEnableButton"
              onClick={(event): void => {
                event.stopPropagation();
                toggleParameter(parameter.id!);
              }}
              shouldShowAsDisabled={!plotIsEnabled}
              tooltipTitle={`${parameter.enabled !== false ? t('timeseries-hide') : t('timeseries-show')} ${parameter.propertyName}`}
            >
              {parameter.enabled !== false && plotIsEnabled ? (
                <Visibility data-testid="enabled-visible" />
              ) : (
                <VisibilityOff data-testid="disabled-visible" />
              )}
            </CustomIconButton>
          </Grid>
          <Grid>
            <Grid sx={[styles.rowText]} title={parameter.collectionId}>
              {parameter.collectionId}
            </Grid>
          </Grid>
        </Grid>
      }
    >
      <Grid
        container
        direction="column"
        spacing={2}
        sx={{ padding: '16px 10px' }}
      >
        <Grid> {t('timeseries-collection')}</Grid>
        <Grid sx={[styles.rowText]} title={parameter.collectionId}>
          {parameter.collectionId}
        </Grid>

        <Grid> {t('timeseries-parameter')}</Grid>
        <Grid>
          <ParameterNameSelect
            parameter={parameter}
            patchParameter={patchParameter}
            rowIsEnabled={plotIsEnabled || rowIsEnabled}
            t={t}
          />
        </Grid>

        <Grid> {t('timeseries-style')}</Grid>
        <Grid>
          <ParameterTypeSelect
            parameter={parameter}
            patchParameter={patchParameter}
            rowIsEnabled={plotIsEnabled || rowIsEnabled}
            t={t}
          />
        </Grid>

        <Grid> {t('timeseries-color')}</Grid>
        <Grid sx={{ width: '100%' }}>
          <ParameterColorSelect
            parameter={parameter}
            patchParameter={patchParameter}
            rowIsEnabled={plotIsEnabled || rowIsEnabled}
            t={t}
            useEntireWidth={true}
          />
        </Grid>

        <Grid> {t('timeseries-opacity')}</Grid>
        <Grid>
          <ParameterOpacitySelect
            parameter={parameter}
            patchParameter={patchParameter}
            rowIsEnabled={plotIsEnabled || rowIsEnabled}
            t={t}
          />
        </Grid>

        <Grid> {t('timeseries-dimension')}</Grid>
        <Grid>
          <ParameterDimensionSelect
            parameter={parameter}
            patchParameter={patchParameter}
            rowIsEnabled={plotIsEnabled || rowIsEnabled}
            t={t}
          />
        </Grid>

        <Grid>
          <Grid
            container
            sx={{
              justifyContent: 'space-evenly',
            }}
          >
            <Grid>
              <ParameterInfoButtonConnect
                panelId={panelId}
                parameter={parameter}
              />
            </Grid>
            <Grid>
              <CustomIconButton
                data-testid="accordionDuplicateButton"
                onClick={(): void => {
                  addParameter(parameter);
                }}
                shouldShowAsDisabled={!plotIsEnabled}
                tooltipTitle={`Duplicate ${parameter.propertyName}`}
              >
                <Copy />
              </CustomIconButton>
            </Grid>
            <Grid>
              <CustomIconButton
                data-testid="accordionDeleteButton"
                onClick={(): void => {
                  deleteParameter(parameter.id!);
                }}
                shouldShowAsDisabled={!plotIsEnabled}
                tooltipTitle={`${t('timeseries-remove')} ${parameter.propertyName}`}
              >
                <Delete />
              </CustomIconButton>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </CustomAccordion>
  );
};

export default TimeSeriesManagerRowAccordion;
